import type {
  PaginatedResponse,
  ProjectImage,
  ProjectRecord,
} from "@/types/api";

/**
 * Normalize a record's `image` to the canonical shape the records **list**
 * returns (`image` + `thumbnail`), the shape every grid and offline-asset-cache
 * reader expects.
 *
 * The records list is served at API v1.1 (`{ image, thumbnail, ... }`), but the
 * create / sync / edit endpoints are v1.2 — their `image` object exposes
 * `annotated_url` / `thumbnail_url` (and the other `*_url` derivatives) and
 * omits the `image` / `thumbnail` keys. Splicing a raw v1.2 response into the
 * v1.1 list cache leaves `record.image.image === undefined`, which the grid
 * reads as a missing src and (via `OfflineImage`) crashed the Photos area.
 *
 * This maps the v1.2 derivatives onto the canonical fields — the v1.1 `image`
 * field *is* the annotated derivative (`annotated_url`); `thumbnail` is
 * `thumbnail_url` — while keeping all `*_url` fields additive so the re-edit
 * modal still finds them. It is idempotent (a v1.1 record passes through
 * unchanged) and copies rather than mutates. Mirrors the `shareRecord*`
 * adapters that normalize the public share shape the same way.
 *
 * See [[offline-record-outbox]] § Success and [[offline-image.spec]].
 */
/**
 * Normalize a single `ProjectImage` to the canonical `{ image, thumbnail }`
 * shape, mapping the v1.2 `*_url` derivatives onto the v1.1 fields. Idempotent.
 */
function normalizeImageShape(img: ProjectImage): ProjectImage {
  return {
    ...img,
    // `image` uses `||` not `??`: a v1.2 record has no `image` key (so it is
    // `undefined`), and an empty-string `image` is not a usable URL either —
    // both should fall through to the annotated derivative. `thumbnail` uses
    // `??`: it is legitimately `string | null`, and a present empty string is
    // never produced upstream, so only null/undefined should fall through.
    image: img.image || img.annotated_url || "",
    thumbnail: img.thumbnail ?? img.thumbnail_url ?? null,
  };
}

export function normalizeRecordImageShape(record: ProjectRecord): ProjectRecord {
  return {
    ...record,
    // Normalize the legacy cover only when present. The v2.0 read carries no
    // top-level `image` (the cover lives at `images[0]`), so guarding here
    // avoids passing `undefined` to `normalizeImageShape`. The cover is never
    // synthesized from `images[0]` — it is resolved at read time via
    // `coverImage()`.
    ...(record.image ? { image: normalizeImageShape(record.image) } : {}),
    // v2.0 multi-photo read: normalize every photo in the set the same way.
    // Absent on v1.x responses.
    ...(record.images ? { images: record.images.map(normalizeImageShape) } : {}),
  };
}

/**
 * Normalize every record in a paginated list response to the canonical image
 * shape. The records list is read at API **v2.0**, whose `images[]` elements
 * carry only the `*_url` derivatives (`annotated_url` / `thumbnail_url`) and no
 * top-level `image`. A raw page therefore leaves `coverImage(record).image`
 * (and `.thumbnail`) undefined, so the grid paints an empty `src` — a gray
 * tile — and the fullscreen viewer opens with no photo.
 *
 * Mapping at the fetch boundary keeps one canonical shape across every reader:
 * the grid, the offline persister/cache, the asset downloader
 * (`recordDownloadsFrom`, which reads `img.thumbnail ?? img.image`), and every
 * `coverImage` / `recordImages` consumer. Idempotent (a v1.1 page passes
 * through unchanged) and copies rather than mutates.
 */
export function normalizeRecordsPage(
  page: PaginatedResponse<ProjectRecord>,
): PaginatedResponse<ProjectRecord> {
  return { ...page, results: page.results.map(normalizeRecordImageShape) };
}
